import { Dispatch } from 'redux';
import { trackAdvisorChatOperation } from '../../observability/rumOperationBridge';
import {
  appendToStreamedMessage,
  appendThinkingProcessToStreamedMessage,
  completeStreamedMessage,
  createChatSessionDispatch,
  setStreamingInProgress,
  startNewStreamedMessage,
  updateSocketLoading
} from './chatAdvisorSlice.slice';
import { Advisor } from '../advisor/advisortypes';
import { getValidTokenForChat } from '../../utils/chatTokenUtils';
import { getConversationRegionChatMetadata } from '../../utils/conversationRegionMetadata';
import type { ChatSourceItem } from './chatAdvisorRestService';

// Interface for the streaming chunk format
interface StreamingChunk {
  a: string; // action type (either "text", "json", or "event")
  v: string | EventData; // value (text content, JSON string, or event data)
}

// Interface for event data (thinking process)
interface EventData {
  type: string; // Event type from backend
  message: string; // Event message from backend
  duration_sec?: number; // Duration in seconds from backend
}

// Helper to check if advisor supports streaming
export const isStreamingAdvisor = (advisor: Advisor | null | undefined): boolean => {
  return advisor?.is_stream === true;
};

// Parse a chunk of streaming data from the custom format
const parseStreamingChunk = (chunk: string): StreamingChunk | null => {
  try {
    // Clean up the chunk - remove 'data:' prefix if present
    const cleanChunk = chunk.trim().startsWith('data:')
      ? chunk.trim().substring(5).trim()
      : chunk.trim();

    // Skip if the chunk is empty after cleaning
    if (!cleanChunk) return null;

    // Parse the JSON chunk
    const parsedChunk = JSON.parse(cleanChunk);

    // Validate if it matches our expected format
    // v can be either string (for text) or object (for events/json)
    if (typeof parsedChunk.a === 'string' && parsedChunk.v !== undefined) {
      return parsedChunk as StreamingChunk;
    }
  } catch (error) {
    // If it's not valid JSON, just return null
    console.error('Error parsing streaming chunk:', error);
  }
  return null;
};

// Process a single chunk of streaming data with first chunk handling
const processStreamingChunkWithContext = (
  chunk: string | { a: string; v: string | EventData },
  dispatch: Dispatch,
  isFirstTextChunk: { value: boolean },
  thinkingProcesses: { type: string; message: string; duration_sec?: number }[]
) => {
  try {
    // Handle both string and object formats
    let parsedChunk: StreamingChunk | null = null;

    if (typeof chunk === 'string') {
      // Parse string chunks - parseStreamingChunk now handles 'data:' prefix
      parsedChunk = parseStreamingChunk(chunk);
    } else if (typeof chunk === 'object' && chunk !== null) {
      // Direct object format
      parsedChunk = chunk as StreamingChunk;
    }

    // If it's not in the expected format or is empty, ignore it
    if (!parsedChunk) {
      return false; // Return false to indicate no content was processed
    }

    // Debug log to see what we're processing

    // For "event" action type, track thinking process
    if (parsedChunk.a === 'event' && typeof parsedChunk.v === 'object' && parsedChunk.v !== null) {
      const eventData = parsedChunk.v;
      
      // Validate that eventData has required fields
      if (!eventData.type || !eventData.message) {
        console.warn('Invalid event data - missing type or message:', eventData);
        return false;
      }

      // Validate that type and message are strings
      if (typeof eventData.type !== 'string' || typeof eventData.message !== 'string') {
        console.warn('Invalid event data - type or message is not a string:', eventData);
        return false;
      }

      // Ensure type and message are not empty strings
      const trimmedType = eventData.type.trim();
      const trimmedMessage = eventData.message.trim();
      
      if (!trimmedType || !trimmedMessage) {
        console.warn('Invalid event data - empty type or message:', eventData);
        return false;
      }

      // Directly use type and message from the event data
      const thinkingStep = {
        type: trimmedType,
        message: trimmedMessage,
        ...(eventData.duration_sec != null && { duration_sec: Number(eventData.duration_sec) })
      };

      thinkingProcesses.push(thinkingStep);
      
      // Dispatch thinking process immediately to update UI during streaming
      dispatch(appendThinkingProcessToStreamedMessage(thinkingStep));

      return false; // Event doesn't count as text content
    }

    // For "text" action type, append the text to the current message
    if (parsedChunk.a === 'text' && typeof parsedChunk.v === 'string') {

      // Ensure proper spacing between words
      let textToAppend = parsedChunk.v;

      // For the first text chunk, don't add extra spacing
      if (isFirstTextChunk.value) {
        isFirstTextChunk.value = false;
        // First chunk - use as is, but ensure it's not empty
        if (!textToAppend.trim()) {
          return false;
        }
      } else {
        // Subsequent chunks - ensure proper word separation
        if (textToAppend &&
          !textToAppend.startsWith(' ') &&
          !textToAppend.startsWith('\n') &&
          !/^[.,!?;:]/.test(textToAppend)) {
          // Check if this might need a space (simple heuristic)
          const needsSpace = /^[a-zA-Z0-9]/.test(textToAppend);
          if (needsSpace) {
            textToAppend = ' ' + textToAppend;
          }
        }
      }

      dispatch(appendToStreamedMessage(textToAppend));
      return true; // Return true to indicate content was processed
    }

    return false; // No text content processed
  } catch (error) {
    console.error('Error processing streaming chunk:', error);
    return false;
  }
};

// Process a single line from the stream with proper JSON handling
const processStreamLine = (
  trimmedLine: string,
  dispatch: Dispatch,
  isFirstTextChunkRef: { value: boolean },
  currentJsonMetadata: string | null,
  thinkingProcesses: { type: string; message: string; duration_sec?: number }[]
): { contentProcessed: boolean; jsonMetadata: string | null; isDone?: boolean } => {
  let contentProcessed = false;
  let jsonMetadata = currentJsonMetadata;

  // Process all data lines
  if (trimmedLine.startsWith('data:')) {

    // Check for the done message
    if (trimmedLine.includes('"a": "done"') || trimmedLine.includes('"a":"done"')) {
      return { contentProcessed: false, jsonMetadata, isDone: true };
    }

    // Process the chunk
    contentProcessed = processStreamingChunkWithContext(trimmedLine, dispatch, isFirstTextChunkRef, thinkingProcesses);

    // Check if this is a JSON metadata chunk and save it for final processing
    if (trimmedLine.includes('"a": "json"') || trimmedLine.includes('"a":"json"')) {
      // Parse from the cleaned line
      const cleanedLine = trimmedLine.substring(5).trim(); // remove 'data:' prefix
      try {
        const parsed = JSON.parse(cleanedLine);
        if (parsed?.a === 'json') {
          jsonMetadata = parsed.v;
        }
      } catch (e) {
        console.error('Error parsing JSON chunk:', e);

      }
    }
  }

  return { contentProcessed, jsonMetadata, isDone: false };
};

export const formatAdvisorResponseData = (jsonMetadata: string | null) => {
  const advisorUIMessage: any = {
    isAdvisor: true,
    isUser: false,
    isStreaming: false,
    aiResponseReceivedTime: Date.now().toString(),
    metaData: [],  // Initialize as array to match expected format
    isFeedbackAllowed: false,
    intent_and_entities: {},
    feedback: '',
    displacement: [],
    comment_history: [],
    rephrased_query: '',
    comment: '',
    advisor_name: '',
    explainanswer: {
      answer: '',
      metaData: {},
      rephrased_query: '',
      reranked_context: {},
      retrieved_context: {}
    }
  };

  if (jsonMetadata) {
    try {
      const metadataObj = typeof jsonMetadata === 'string'
        ? JSON.parse(jsonMetadata)
        : jsonMetadata;

      if (metadataObj?.intent_and_entities) {
        advisorUIMessage.intent_and_entities = metadataObj.intent_and_entities;
      }
      
      // Extract is_ask and clarifying_question_config from root level metadata
      if (metadataObj?.is_ask !== undefined) {
        advisorUIMessage.is_ask = metadataObj.is_ask;
      }
      if (metadataObj?.clarifying_question_config) {
        advisorUIMessage.clarifying_question_config = metadataObj.clarifying_question_config;
      }
      if (metadataObj?.multi_select !== undefined) {
        advisorUIMessage.multi_select = metadataObj.multi_select;
      }
      
      // Also check if response is a string that needs parsing (for REST API responses)
      if (metadataObj?.response && typeof metadataObj.response === 'string') {
        try {
          const parsedResponse = JSON.parse(metadataObj.response);
          if (parsedResponse?.is_ask !== undefined && advisorUIMessage.is_ask === undefined) {
            advisorUIMessage.is_ask = parsedResponse.is_ask;
          }
          if (parsedResponse?.clarifying_question_config && !advisorUIMessage.clarifying_question_config) {
            advisorUIMessage.clarifying_question_config = parsedResponse.clarifying_question_config;
          }
          if (parsedResponse?.multi_select !== undefined && advisorUIMessage.multi_select === undefined) {
            advisorUIMessage.multi_select = parsedResponse.multi_select;
          }
        } catch (e) {
          console.error('Error parsing JSON metadata:', e);
        }
      }
      
      if (metadataObj.predictions && metadataObj.predictions[0]) {
        const prediction = metadataObj.predictions[0];

        // Format the metadata as an array of objects with single key-value pairs
        // to match the format expected by the existing code
        const metaArray: any = [];
        if (prediction.meta) {
          for (let i = 0; i < prediction.meta.length; i++) {
            const item = prediction.meta[i];
            // Skip if item is not an object
            if (typeof item !== 'object' || item === null) continue;

            const keys = Object.keys(item);
            if (keys.length > 0) {
              const key = keys[0];
              metaArray.push({ [key]: item[key] });
            }
          }
        }

        advisorUIMessage.metaData = metaArray;
        advisorUIMessage.isFeedbackAllowed = metaArray.length > 0;

        if (metadataObj?.filters !== undefined && !metaArray.some((item: any) => item.filters !== undefined)) {
          advisorUIMessage.metaData.push({ filters: metadataObj.filters });
        }

        const metadataMap = () => {
          // start with an empty map
          return metaArray.reduce((acc: any, obj: any) => {
            const [key, value] = Object.entries(obj)[0] || [];
            if (key) acc[key] = value;
            return acc;
          }, {});
        }

        const metaDataObj = metadataMap();

        // Extract other fields
        const rephrased_query = metaDataObj?.rephrased_query ?? '';
        const metadata = metaDataObj?.metadata ?? [];
        const source_ids = metaDataObj?.source_ids ?? [];
        const message_id = metaDataObj?.message_id ?? '';
        const advisor_name = metaDataObj?.advisor_name ?? '';

        advisorUIMessage.rephrased_query = rephrased_query;
        advisorUIMessage.displacement = metadata;
        // Do not overwrite streamed text with an empty/whitespace value when metadata
        // is partial or missing answer fields in some streaming responses.
        if (typeof prediction.answer === 'string' && prediction.answer.trim().length > 0) {
          advisorUIMessage.message = prediction.answer;
        }
        advisorUIMessage.advisor_name = advisor_name;

        // Populate explainanswer for compatibility
        advisorUIMessage.explainanswer = {
          answer: prediction.answer || '',
          rephrased_query: rephrased_query,
          retrieved_context: prediction.retrieved_context?.source_ids || [],
          reranked_context: source_ids,
          metaData: metadata,
        };

        // Add message_id for reference
        if (message_id) {
          advisorUIMessage.metaData.push({ message_id });
        }

        // Ensure source_ids are properly structured in metaData array
        // The UI expects source_ids to be in metaData[1] for viewKnowledge to work
        if (source_ids && source_ids.length > 0) {
          // Find if source_ids already exists in metaData
          const sourceIdsIndex = advisorUIMessage.metaData.findIndex((item: any) => item.source_ids);
          if (sourceIdsIndex === -1) {
            // Add source_ids to metaData array - ensure it's at index 1 for UI compatibility
            if (advisorUIMessage.metaData.length === 0) {
              advisorUIMessage.metaData.push({}); // placeholder for index 0
            }
            if (advisorUIMessage.metaData.length === 1) {
              advisorUIMessage.metaData.push({ source_ids });
            } else {
              advisorUIMessage.metaData[1] = { source_ids };
            }
          }
        }


      }
      if (metadataObj?.filters !== undefined) {
        const hasFilters = advisorUIMessage.metaData.some((item: any) => item.filters !== undefined);
        if (!hasFilters) {
          advisorUIMessage.metaData.push({ filters: metadataObj.filters });
        }
      }
    } catch (e) {
      console.error('Error parsing JSON metadata:', e);
    }
  }

  return advisorUIMessage

}


// Process the final response with metadata
const processStreamingCompletion = (
  jsonMetadata: string | null, 
  dispatch: Dispatch,
  thinkingProcesses?: { type: string; message: string; duration_sec?: number }[]
) => {
  try {

    const advisorUIMessage = formatAdvisorResponseData(jsonMetadata);

    // Add thinking processes if available and valid
    if (thinkingProcesses && Array.isArray(thinkingProcesses) && thinkingProcesses.length > 0) {
      // Filter out any invalid thinking steps (extra safety check)
      const validThinkingProcesses = thinkingProcesses.filter(step => 
        step && 
        typeof step === 'object' && 
        typeof step.type === 'string' && 
        typeof step.message === 'string' &&
        step.type.trim() !== '' &&
        step.message.trim() !== ''
      );

      if (validThinkingProcesses.length > 0) {
        advisorUIMessage.thinkingProcesses = validThinkingProcesses;
      }
    }

    // Complete the streamed message with the processed metadata
    dispatch(completeStreamedMessage(advisorUIMessage));
    dispatch(updateSocketLoading(false));
    dispatch(setStreamingInProgress(false));
  } catch (error) {
    console.error('Error processing streaming completion:', error);
    dispatch(setStreamingInProgress(false));
  }
};

/**
 * Sends a message to the streaming API and processes the streamed response
 */
export const sendStreamingMessage = async (
  apiUrl: string,
  message: string,
  senderId: string,
  advisorId: string,
  sources: ChatSourceItem[],
  mentions: any,
  token: string,
  dispatch: Dispatch,
  apiKey?: string,
  deepThinkMode: boolean = false,
  isAuthenticated: boolean = true,
  fetchLatestGuestToken?: () => Promise<string | null | undefined>,
  sessionId?: string
) => {
  // Capture the destination before the first await; focus may change while bytes arrive.
  dispatch = sessionId ? createChatSessionDispatch(dispatch, sessionId) : dispatch;

  // Start loading state and create initial message
  dispatch(updateSocketLoading(true));
  dispatch(setStreamingInProgress(true));

  // Validation check - if required parameters are missing, exit early
  if (!apiUrl) {
    console.error("Missing required parameters for streaming", { apiUrl });
    dispatch(updateSocketLoading(false));
    dispatch(setStreamingInProgress(false));
    return;
  }

  // Create initial streaming message with a placeholder to prevent empty UI
  dispatch(startNewStreamedMessage({
    isAdvisor: true,
    isUser: false,
    message: '', // Start with empty message, will be populated by streaming
    metaData: [], // Initialize as array to match expected format
    isFeedbackAllowed: false,
    isStreaming: true,
    aiResponseReceivedTime: Date.now().toString()
  }));

  return trackAdvisorChatOperation({
    operation: 'chat_stream',
    startAttributes: {
      advisor_id: advisorId,
      sender_id: senderId,
      source_count: sources?.length ?? 0,
    },
    run: async () => executeAdvisorChatStream({
      apiUrl,
      message,
      senderId,
      advisorId,
      sources,
      mentions,
      token,
      dispatch,
      apiKey,
      deepThinkMode,
      isAuthenticated,
      fetchLatestGuestToken,
    }),
    completeAttributes: (result) => ({
      message_id: result.message_id,
      data_yielded: result.has_content,
    }),
  });
};

type AdvisorStreamRunResult = Readonly<{
  has_content: boolean;
  message_id: string | null;
}>;

async function executeAdvisorChatStream(params: {
  apiUrl: string;
  message: string;
  senderId: string;
  advisorId: string;
  sources: ChatSourceItem[];
  mentions: any;
  token: string;
  dispatch: Dispatch;
  apiKey?: string;
  deepThinkMode: boolean;
  isAuthenticated: boolean;
  fetchLatestGuestToken?: () => Promise<string | null | undefined>;
}): Promise<AdvisorStreamRunResult> {
  const {
    apiUrl,
    message,
    senderId,
    advisorId,
    sources,
    mentions,
    token,
    dispatch,
    apiKey,
    deepThinkMode,
    isAuthenticated,
    fetchLatestGuestToken,
  } = params;

  // Declare timeout variables at function scope
  let connectionTimeout: NodeJS.Timeout | null = null;
  let contentTimeout: NodeJS.Timeout | null = null;
  let hasReceivedAnyContent = false;
  let jsonMetadata: string | null = null;

  try {
    // For testing with static payload, we can simulate streaming
    // In a real implementation, this would be replaced with an actual API call

    // Decide whether to use mock data or real API call

    const isFirstTextChunkRef = { value: true }; // Track if this is the first text chunk
    let streamStarted = false; // Track if the stream connection has started
    const thinkingProcesses: { type: string; message: string; duration_sec?: number }[] = []; // Track thinking processes

    // Connection timeout (30 seconds)
    connectionTimeout = setTimeout(() => {
      if (!streamStarted) {
        console.warn("Streaming connection timed out (30s) - no stream established");
        processStreamingCompletion(null, dispatch, thinkingProcesses);
      }
    }, 300000);

   
      // Make real API call with streaming
      try {
        // Get a valid token (refresh if needed before making the API call)
        const validToken = await getValidTokenForChat(token, 300, dispatch, {
          isAuthenticated,
          fetchLatestGuestToken
        });

        const tokenToUse = validToken || token;

        const regionMetadata = getConversationRegionChatMetadata();

        const requestData = {
          advisor_id: advisorId,
          user_message: message ?? '',
          sender_id: senderId ?? "123456677888",
          metadata: {
            sources: sources ?? [],
            jwt: '',
            mentions: mentions ?? {},
            ...(regionMetadata ?? {}),
          },
          token: tokenToUse,
          deep_think_mode: deepThinkMode,
          ...(apiKey && { 'x-api-key': apiKey })
        };

        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json, text/event-stream',
            'Authorization': tokenToUse ? `Bearer ${tokenToUse}` : ''
          },
          body: JSON.stringify(requestData)
        };


      const response = await fetch(apiUrl, requestOptions);

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported');
      }

      // Setup the reader for the stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      streamStarted = true; // Mark that the stream connection is established
      if (connectionTimeout) {
        clearTimeout(connectionTimeout); // Clear connection timeout since we're connected
      }

        // Start content timeout now that stream is connected
        contentTimeout = setTimeout(() => {
          if (!hasReceivedAnyContent) {
            console.warn("No content received within 15 mins after stream started");
            processStreamingCompletion(null, dispatch, thinkingProcesses);
          }
        }, 1500000);

        // Buffer to accumulate incomplete lines across multiple reads
        let lineBuffer = '';
        // Tracks the backend's logical "done" marker, which can arrive well before
        // the underlying HTTP stream is physically closed by the server/proxy.
        let doneSignalReceived = false;

      // Process the stream
      while (true) {
        const { done, value } = await reader.read();

          if (done) {
            // Process any remaining data in the buffer
            if (lineBuffer.trim()) {
              const finalLines = lineBuffer.split('\n');
              for (const line of finalLines) {
                if (line.trim()) {
                  const result = processStreamLine(line.trim(), dispatch, isFirstTextChunkRef, jsonMetadata, thinkingProcesses);
                  if (result.jsonMetadata) {
                    jsonMetadata = result.jsonMetadata;
                  }
                }
              }
            }
            break;
          }

          // Decode the chunk and add to buffer
          const text = decoder.decode(value, { stream: true });
          lineBuffer += text;


          // Process complete lines from the buffer
          const lines = lineBuffer.split('\n');
          // Keep the last line in buffer (might be incomplete)
          lineBuffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue; // Skip empty lines

            const result = processStreamLine(trimmedLine, dispatch, isFirstTextChunkRef, jsonMetadata, thinkingProcesses);
            if (result.contentProcessed) {
              hasReceivedAnyContent = true;
              // Clear content timeout since we're receiving content
              if (contentTimeout) {
                clearTimeout(contentTimeout);
                contentTimeout = null;
              }
              // Stop the loading indicator once we start receiving actual content
              dispatch(updateSocketLoading(false));
            }
            if (result.jsonMetadata) {
              jsonMetadata = result.jsonMetadata;
            }
            console.log('Result from processing line:', result, result.isDone ? 'Done signal received' : '');
            // If we received a done signal, stop processing further lines in this chunk
            if (result.isDone) {
              doneSignalReceived = true;
              break;
            }
          }

          // The logical "done" marker means the backend has finished the answer.
          // Stop reading immediately instead of waiting for the transport-level
          // stream to close, which may be delayed indefinitely (keep-alives,
          // proxy buffering, trailing events) and would otherwise leave the UI
          // stuck showing "streaming" even though the full response already arrived.
          if (doneSignalReceived) {
            break;
          }
        }

        if (doneSignalReceived) {
          // Release the underlying connection rather than leaving it open now that
          // we've finished logically; we don't need any further bytes from it.
          try {
            await reader.cancel();
          } catch {
            // Ignore - the stream may already be closed.
          }
        }

        // Process completion after streaming is done
        processStreamingCompletion(jsonMetadata, dispatch, thinkingProcesses);
        
      } catch (fetchError) {
        console.error('Fetch error during streaming:', fetchError);
        // Complete with an error message to the user
        dispatch(appendToStreamedMessage("\n\nI'm sorry, there was an error connecting to the service. Please try again later."));
        
        // Clear timeouts on error
        if (connectionTimeout) {
          clearTimeout(connectionTimeout);
        }
        if (contentTimeout) {
          clearTimeout(contentTimeout);
        }
        processStreamingCompletion(jsonMetadata, dispatch, thinkingProcesses);
        throw fetchError;
      
    }

    return extractAdvisorStreamRunResult(jsonMetadata, hasReceivedAnyContent);
  } catch (error) {
    console.error('Error during streaming request:', error);
    // Clear timeouts on error
    if (connectionTimeout) {
      clearTimeout(connectionTimeout);
    }
    if (contentTimeout) {
      clearTimeout(contentTimeout);
    }

    // End streaming on error and provide user feedback
    dispatch(appendToStreamedMessage("\n\nI apologize, but there was an error processing your request. Please try again."));

    // Complete the message even on error
    const errorMessage = {
      isAdvisor: true,
      isUser: false,
      isStreaming: false,
      aiResponseReceivedTime: Date.now().toString(),
      metaData: [],
      isFeedbackAllowed: false,
      thinkingProcesses: [] // Empty thinking processes for error case
    };
    dispatch(completeStreamedMessage(errorMessage));
    dispatch(updateSocketLoading(false));
    dispatch(setStreamingInProgress(false));
    throw error;
  }
}

function extractAdvisorStreamRunResult(
  jsonMetadata: string | null,
  hasReceivedAnyContent: boolean,
): AdvisorStreamRunResult {
  let message_id: string | null = null;
  if (jsonMetadata) {
    try {
      const meta = typeof jsonMetadata === 'string' ? JSON.parse(jsonMetadata) : jsonMetadata;
      if (typeof meta?.message_id === 'string' && meta.message_id.trim()) {
        message_id = meta.message_id.trim();
      }
    } catch {
      /* ignore */
    }
  }
  return { has_content: hasReceivedAnyContent, message_id };
}
